/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable import/max-dependencies */
import React, { useState, useEffect } from 'react'
import { Box } from '@usap/uicomponents'
import {
  SummaryContainer,
  SummaryHeader,
  SummaryTitle,
  SummaryTorso,
  SummaryTorsoWidget,
} from './SummaryWmo.css'
import SummaryQuantityWmo from './SummaryQuantityWmo'
import SubTotal from './SubTotal'
import SummaryShippingSelector from './SummaryShippingSelector'
import TotalWmo from './Total/TotalWmo'
import CouponInput from './CouponInput'
import Payment from './Payment'
import ProceedToCheckout from './ProceedToCheckout/Regular'
import OtherPaymentOptionsWmo from './ProceedToCheckout/OtherPaymentOptionsWmo'
import ShippingWmo from './Shipping/ShippingWmo'
import HandlingWmo from './Handling/HandlingWmo'
import WarrantyWmo from './Warranty/WarrantyWmo'
import CoreWmo from './Core/CoreWmo'
import CouponDiscount from './CouponDiscount'
import ProductProtection from './ProductProtection/ProductProtection'
import { SummaryLoader } from './SummaryLoader'
import { CarpartsCreditCardSummaryBanner } from '../../../CarpartsCreditCard/CarpartsCreditCardSummaryBanner'
import { CarpartsCreditCardSummaryBannerGold } from '../../../CarpartsCreditCard/CarpartsCreditCardSummaryBannerGold'
import { useCarpartsCreditCardEnabled } from '../../../../hooks/useCarpartsCreditCardEnabled'
import { useTokenContext } from '../../../../contexts/token.context'
import useLoyaltyPointsEstimation from '../../hooks/useLoyaltyPointsEstimation'
import { LoyaltyPointsBanner, LoyaltyPointsBannerSkeleton } from './LoyaltyPointsBanner'
import LoyaltySignInModal from './LoyaltySignInModal'
import LoyaltyRedeemModal from './LoyaltyRedeemModal'
import useIsLoyaltyActiveForCustomer from '../../../../hooks/useIsLoyaltyActiveForCustomer'
import { useLoyaltyMemberSummary } from '../../hooks/useLoyaltyMemberSummary'
import redeemLoyalty from '../../api/loyalty/redeemLoyalty'
import { useShoppingCartContext } from '../../providers/ShoppingCartProvider'
import shoppingCartV2API from '../../api'
import GTM from '../../../../helpers/GTM'

type SummaryWmoProps = {
  data: any
  multiShippingDiscountFlag: boolean
  isCouponOff: boolean
  paymentType: string
  totalAfterTax: number
  isDesktop: boolean
  showLoader: boolean
}
// if a product is both free shipping for members and for everone, this is false
const isFreeShippingForMembers = (item: { inventory : {membershipShippingType: string, shipping: { type: string } } }) => item?.inventory?.membershipShippingType === 'Free' && item?.inventory.shipping.type !== 'Free'

const SummaryWmo: React.FC<SummaryWmoProps> = ({
  data,
  multiShippingDiscountFlag,
  isCouponOff,
  totalAfterTax,
  isDesktop,
  showLoader,
}) => {
  const { summary, modifiers } = data
  const memberType = modifiers?.membership?.hasActiveMembership || false
  const couponDiscount = modifiers?.coupon?.discounts?.find((coupon: { source: string, code: string }) => coupon?.source === 'loyalty') ?? null
  const couponDiscountSku = modifiers?.coupon?.discounts?.find((coupon: { applied: boolean, code: string }) => coupon?.applied === true) ?? null

  const ZIP_MINIMUM_TOTAL = 35
  const PAYPAL_LATER_MINIMUM_TOTAL = 30

  const showZip = totalAfterTax >= ZIP_MINIMUM_TOTAL
  const showPaypalLater = totalAfterTax >= PAYPAL_LATER_MINIMUM_TOTAL

  const hasFreeShippingForMembers = Boolean(
    data?.items?.find(isFreeShippingForMembers),
  )

  const hasAllItemsFreeShippingForMembers = Boolean(
    data?.items?.length > 0 && data.items.every(isFreeShippingForMembers),
  )

  const itemCount = summary?.quantityTotal || 0
  const isCarpartsCreditCardEnabled = useCarpartsCreditCardEnabled()

  const tokenState = useTokenContext()
  const isGuest = !tokenState?.token_number
  const accessToken = tokenState?.accessToken
  const { getCartId, getIdentifier, setCart } = useShoppingCartContext()
  // Per-customer gate — true only when (a) loyalty kill-switch is off AND
  // (b) this customer is actually enrolled in loyalty (cached by the
  // bootstrap). Replaces the f98-only check so an enrolled customer on a
  // device that drew f98=a still sees their cart loyalty UI. Variable
  // name kept for minimal diff downstream.
  const isLoyaltyEnabled = useIsLoyaltyActiveForCustomer()
  const [showRedeemModal, setShowRedeemModal] = useState(false)
  const { totalPoints: memberTotalPoints, loyaltyCardCode } = useLoyaltyMemberSummary({ accessToken, enabled: !isGuest && isLoyaltyEnabled && showRedeemModal })
  const LOYALTY_COUPON_KEY = 'cp_loyalty_coupon_code'
  const [loyaltyCouponCode, setLoyaltyCouponCode] = useState<string | null>(
    () => localStorage.getItem(LOYALTY_COUPON_KEY),
  )
  const appliedCoupon = couponDiscount?.code ?? null
  const isCouponApplied = !!loyaltyCouponCode && appliedCoupon === loyaltyCouponCode
  const { points: estimatedPoints, loading: loyaltyLoading, rewards: loyaltyRewardsRaw, bonus: loyaltyBonus } = useLoyaltyPointsEstimation(
    isLoyaltyEnabled
      ? { items: data.items, memberType, accessToken, cartTotal: summary?.estSubTotal, itemSku: couponDiscountSku?.itemSku || null, couponCode: couponDiscountSku?.code ?? appliedCoupon }
      : { items: null, memberType, accessToken, cartTotal: summary?.estSubTotal, itemSku: couponDiscountSku?.itemSku || null, couponCode: couponDiscountSku?.code ?? appliedCoupon })
  const [loyaltyPoints, setLoyaltyPoints] = useState<number | null>(null)
  const loyaltyRewards = loyaltyRewardsRaw as Array<{ id?: string, points?: number, rewardValue?: number, rewardId?: string }> | null
  const [showSignInModal, setShowSignInModal] = useState(false)

  useEffect(() => {
    setLoyaltyPoints(estimatedPoints)
  }, [estimatedPoints])

  const redeemableAmount = loyaltyRewards?.[0]?.rewardValue || 0
  const pointsPerDollar = loyaltyRewards?.[loyaltyRewards.length - 1]?.points || 0

  useEffect(() => {
    if (appliedCoupon) {
      localStorage.setItem(LOYALTY_COUPON_KEY, appliedCoupon)
      setLoyaltyCouponCode(appliedCoupon)
    }
  }, [appliedCoupon])

  return (
    <SummaryContainer>
      <SummaryLoader showLoader={showLoader} />
      <SummaryHeader>
        <SummaryTitle>
          Order Summary
          <SummaryQuantityWmo quantity={summary.quantityTotal} />
        </SummaryTitle>
        <SummaryShippingSelector />
      </SummaryHeader>
      <SummaryTorso>
        <SubTotal data={summary} />
        {parseFloat(summary.warrantyTotal) > 0 && (
          <Box>
            <WarrantyWmo data={summary} />
          </Box>
        )}

        {(summary?.warrantyProductTotal && parseFloat(summary.warrantyProductTotal) > 0) && (
          <Box>
            <ProductProtection data={summary} />
          </Box>
        )}

        {parseFloat(summary.coreTotal) > 0 && (
          <Box>
            <CoreWmo data={summary} />
          </Box>
        )}

        <Box>
          <ShippingWmo
            data={summary}
            multiShippingDiscountFlag={multiShippingDiscountFlag}
          />
        </Box>

        {hasFreeShippingForMembers && isCarpartsCreditCardEnabled && !isLoyaltyEnabled && (
          <Box>
            <CarpartsCreditCardSummaryBanner
              hasAllItemsFreeShippingForMembers={hasAllItemsFreeShippingForMembers}
              itemCount={itemCount}
            />
          </Box>
        )}

        {parseFloat(summary.handling.estimated) > 0 && (
          <Box>
            <HandlingWmo data={summary} />
          </Box>
        )}

        {parseFloat(summary?.coupon?.discount) > 0 && (
          <Box>
            <CouponDiscount data={data} />
          </Box>
        )}

      </SummaryTorso>
      <SummaryTorsoWidget>
        <Box>
          <TotalWmo data={summary} />
        </Box>

        {isLoyaltyEnabled && (
          <Box>
            {loyaltyLoading && <LoyaltyPointsBannerSkeleton />}
            {!loyaltyLoading && loyaltyPoints !== null && (
              <>
                <LoyaltyPointsBanner
                  points={loyaltyPoints as unknown as number}
                  onSignInClick={() => setShowSignInModal(true)}
                  onRedeemClick={() => setShowRedeemModal(true)}
                  isGuest={isGuest}
                  memberType={memberType}
                  loyaltyRewards={loyaltyPoints}
                  redeemableAmount={redeemableAmount}
                  isCouponApplied={isCouponApplied}
                  loyaltyBonus={loyaltyBonus}
                />
                {isCarpartsCreditCardEnabled && !memberType && (
                  <div style={{ marginTop: '12px', marginBottom: '6px', width: '100%' }}>
                    <CarpartsCreditCardSummaryBannerGold href={'/membership'} hasFreeShippingForMembers={hasFreeShippingForMembers} onClick={() => GTM?.creditCardInfoModalImpression()} />
                  </div>
                )}
              </>
            )}
          </Box>
        )}

        {!isCouponOff && (
          <Box>
            <CouponInput data={data} />
          </Box>
        )}

        <Payment
          totalAfterTax={totalAfterTax}
          isDesktop={isDesktop}
          isWmoEnabled
          showZip={showZip}
          showPaypalLater={showPaypalLater}
        />

        <Box>
          <ProceedToCheckout />
          <OtherPaymentOptionsWmo />
        </Box>
      </SummaryTorsoWidget>
      <LoyaltySignInModal open={showSignInModal} onOpenChange={setShowSignInModal} />
      <LoyaltyRedeemModal
        open={showRedeemModal}
        onOpenChange={setShowRedeemModal}
        totalPoints={memberTotalPoints ?? 0}
        redeemableAmount={redeemableAmount ?? 0}
        orderTotal={totalAfterTax}
        onRedeem={({ selectedAmount }) => {
          const reward = loyaltyRewards?.find(r => +(r?.rewardValue || 0) === selectedAmount)
          GTM?.fireLoayaltyEventCartCheckout({ event: 'cart_redeem_modal_cta_engagement' })
          return redeemLoyalty({ accessToken, rewardId: reward?.id, loyaltyCardId: loyaltyCardCode })
            .then(res => {
              const couponCode = res?.data?.data?.voucherCode
              if (!couponCode) return Promise.resolve()
              localStorage.setItem(LOYALTY_COUPON_KEY, couponCode)
              setLoyaltyCouponCode(couponCode)
              setLoyaltyPoints(null)
              return shoppingCartV2API.coupons.apply(
                getIdentifier(),
                getCartId(),
                { coupon: { code: couponCode } },
              ).then(setCart)
            })
            .then(() => setShowRedeemModal(false))
            .catch((err) => console.error('Redeem failed', err))
        }}
        pointsPerDollar={pointsPerDollar}
      />
    </SummaryContainer>
  )
}

export default SummaryWmo
